import { Input } from '@/components/ui/FormControls';
import PermissionGate from '@/components/common/PermissionGate';
import { LockKeyhole, RotateCcw, Trash2 } from 'lucide-react';
import type { RecycleEntity, RecycleRow } from '@klothiq/domain';

export const recycleButton =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50';

export default function RecycleRecordCard({
  row,
  entity,
  selected,
  disabled,
  onSelect,
  onRestore,
  onDelete,
  onViewSales,
}: {
  row: RecycleRow;
  entity: RecycleEntity;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  onRestore: () => void;
  onDelete: () => void;
  onViewSales: () => void;
}) {
  const timestamp = row.date
    ? new Date(row.date).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Not recorded';
  return (
    <article className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5">
      <div className="flex items-start gap-3">
        <label className="flex min-h-11 min-w-11 shrink-0 items-center justify-center">
          <Input
            type="checkbox"
            aria-label={`Select ${row.label}`}
            checked={selected}
            disabled={disabled || !row.can_purge}
            onChange={onSelect}
            className="h-5 w-5 accent-primary"
          />
        </label>
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h2 className="break-words text-base font-semibold">{row.label}</h2>
              <p className="mt-1 break-words text-sm text-muted-foreground">{row.secondary}</p>
            </div>
            {row.amount !== null && (
              <p className="shrink-0 text-base font-semibold tabular-nums">
                {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(
                  Number(row.amount),
                )}
              </p>
            )}
          </div>
          <dl className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
            <div>
              <dt className="font-medium">
                {entity === 'products' ? 'Last updated' : entity === 'sales' ? 'Deleted' : 'Voided'}
              </dt>
              <dd className="mt-1">{timestamp}</dd>
            </div>
            <div>
              <dt className="font-medium">Reason</dt>
              <dd className="mt-1 break-words">{row.reason || 'Not recorded'}</dd>
            </div>
          </dl>
          {!row.can_purge && (
            <div className="mt-3 flex gap-2 rounded-lg border border-amber-500/25 bg-amber-500/5 p-3 text-sm">
              <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div>
                <p className="font-medium">
                  {row.block_code === 'PROTECTED_PAYMENT'
                    ? 'Managed by sale or purchase'
                    : 'Retained in the recycle bin'}
                </p>
                <p className="mt-1 text-muted-foreground">{row.block_message}</p>
                {row.parent_sale_deleted && (
                  <button
                    className="mt-2 min-h-11 text-primary underline underline-offset-4"
                    disabled={disabled}
                    onClick={onViewSales}
                  >
                    View deleted sales
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <PermissionGate permission="recycle.restore"><button
              className={`${recycleButton} border-border hover:bg-muted`}
              disabled={disabled || !row.can_restore}
              onClick={onRestore}
            >
              <RotateCcw className="h-4 w-4" />
              Restore
            </button></PermissionGate>
            <PermissionGate permission="recycle.purge"><button
              className={`${recycleButton} border-destructive/25 text-destructive hover:bg-destructive/10`}
              disabled={disabled || !row.can_purge}
              onClick={onDelete}
            >
              <Trash2 className="h-4 w-4" />
              Permanently delete
            </button></PermissionGate>
          </div>
        </div>
      </div>
    </article>
  );
}
